/**
 * useVerifyEmail Hook
 *
 * Verifies email with code using URQL mutation.
 */

import { useCallback } from "react";
import { useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";

// =============================================================================
// MUTATION DOCUMENT
// =============================================================================

const VerifyEmailDocument = `
  mutation VerifyEmailLink($email: String!, $code: String!) {
    verifyEmailLink(email: $email, code: $code)
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface UseVerifyEmailResult {
  verifyEmail: (email: string, code: string) => Promise<boolean>;
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Verify email with verification code.
 */
export function useVerifyEmail(): UseVerifyEmailResult {
  const [result, executeMutation] = useMutation<
    { verifyEmailLink: boolean },
    { email: string; code: string }
  >(VerifyEmailDocument);

  const verifyEmail = useCallback(
    async (email: string, code: string): Promise<boolean> => {
      try {
        const response = await executeMutation({
          email: email.toLowerCase().trim(),
          code,
        });

        if (response.error) {
          throw new Error(
            response.error.graphQLErrors[0]?.message ||
              response.error.message ||
              "Failed to verify email"
          );
        }

        errorTracking.addBreadcrumb({
          category: "profile",
          message: "Email verified",
        });

        return response.data?.verifyEmailLink ?? false;
      } catch (err) {
        const verifyError =
          err instanceof Error ? err : new Error("Failed to verify email");

        errorTracking.captureError(verifyError, {
          tags: { feature: "profile", action: "verifyEmail" },
        });

        throw verifyError;
      }
    },
    [executeMutation]
  );

  return {
    verifyEmail,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
